/**
 * "Mantener pulsado" en el movil y "click derecho" en el PC, que son el mismo
 * gesto: pedirle mas opciones a algo sin salir de donde estas.
 *
 * Tres cosas que hay que hacer bien o el gesto sale mal:
 *
 * 1. **Un dedo que se mueve esta haciendo scroll, no pulsando.** La rejilla de
 *    la biblioteca se recorre arrastrando justo por encima de las portadas, y
 *    sin el margen de movimiento cada scroll lento abriria la hoja. Diez
 *    pixeles bastan para separar las dos intenciones.
 * 2. **Al soltar, iOS manda un `click`.** Las portadas son enlaces, asi que
 *    sin tragarse ese click se abriria la ficha detras de la hoja. Por eso
 *    queda la marca `disparado` hasta que llega.
 * 3. **Safari saca su propio menu** (copiar enlace, compartir) al mantener
 *    pulsado sobre un `<a>` o una imagen. Eso no se apaga desde JavaScript:
 *    va en el CSS de la tarjeta con `-webkit-touch-callout: none`.
 *
 * En el iPhone **no existe el evento `contextmenu`**, y en el PC el click
 * derecho no produce click despues. Cada camino cubre un aparato.
 */

import { useCallback, useEffect, useRef } from 'react'
import type { MouseEvent, TouchEvent } from 'react'

/** Lo que tarda en considerarse una pulsacion larga. */
const ESPERA = 480

/** Cuanto puede moverse el dedo antes de que cuente como scroll. */
const MARGEN = 10

export function useMantenerPulsado(alActivar: () => void) {
  const temporizador = useRef<number | null>(null)
  const origen = useRef<{ x: number; y: number } | null>(null)
  const disparado = useRef(false)

  const cancelar = useCallback(() => {
    if (temporizador.current !== null) {
      clearTimeout(temporizador.current)
      temporizador.current = null
    }
  }, [])

  // Si el componente se va mientras corre la cuenta atras, la hoja no debe
  // abrirse en la pantalla siguiente.
  useEffect(() => cancelar, [cancelar])

  return {
    onContextMenu(evento: MouseEvent<HTMLElement>) {
      evento.preventDefault()
      alActivar()
    },
    onTouchStart(evento: TouchEvent<HTMLElement>) {
      const dedo = evento.touches[0]
      if (!dedo) return
      disparado.current = false
      origen.current = { x: dedo.clientX, y: dedo.clientY }
      cancelar()
      temporizador.current = window.setTimeout(() => {
        temporizador.current = null
        disparado.current = true
        alActivar()
      }, ESPERA)
    },
    onTouchMove(evento: TouchEvent<HTMLElement>) {
      const dedo = evento.touches[0]
      if (!dedo || !origen.current) return
      const lejos =
        Math.abs(dedo.clientX - origen.current.x) > MARGEN ||
        Math.abs(dedo.clientY - origen.current.y) > MARGEN
      if (lejos) cancelar()
    },
    onTouchEnd: cancelar,
    onTouchCancel: cancelar,
    onClick(evento: MouseEvent<HTMLElement>) {
      if (!disparado.current) return
      disparado.current = false
      evento.preventDefault()
      evento.stopPropagation()
    },
  }
}
